// src/core/PrivateRoute.js
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';

// Profile completion check utility
const getProfileCompletionStatus = (user) => {
  if (!user) return { isComplete: false, nextStep: 1 };
  
  const hasAccountType = user.accountType && user.accountType.trim() !== '';
  const hasPhone = user.phone && user.phone.trim() !== '';
  const hasGender = user.gender && user.gender.trim() !== '' && 
                   (user.gender !== 'other' || user.accountType === 'organization');
  const hasBirthday = user.metaData?.birthday || user.metaData?.hasCompletedBirthday;
  // eslint-disable-next-line no-unused-vars
  const hasDocuments = user.metaData?.documents && user.metaData.documents.length > 0;
  
  // Step 1: Account Type
  if (!hasAccountType) {
    return { isComplete: false, nextStep: 1 };
  }

  // Step 2: Phone (required for all)
  if (!hasPhone) {
    return { isComplete: false, nextStep: 2 };
  }

  // Different flows based on account type
  if (user.accountType === "organization") {
    // Organizations: account type → phone → documents (optional)
    // Profile is complete after phone, but documents step is still available
    return { isComplete: true, nextStep: 5, canAccessDocuments: true };
  } else if (user.accountType === "player") {
    // Players: account type → phone → gender → birthday → documents (optional)
    if (!hasGender) {
      return { isComplete: false, nextStep: 3 };
    }
    if (!hasBirthday) {
      return { isComplete: false, nextStep: 4 };
    }
    // Profile is complete after birthday, but documents step is still available
    return { isComplete: true, nextStep: 5, canAccessDocuments: true };
  } else {
    // Others (parent, talent scout): account type → phone → gender → documents (optional)
    if (!hasGender) {
      return { isComplete: false, nextStep: 3 };
    }
    // Profile is complete after gender, but documents step is still available
    return { isComplete: true, nextStep: 5, canAccessDocuments: true };
  }
};

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  console.log("🛡️ PrivateRoute Check:", { isAuthenticated, user, loading, currentPath: window.location.pathname });

  if (loading) {
    return <div>Loading...</div>; // Or your loading component
  }

  if (!isAuthenticated) {
    console.log("❌ Not authenticated - redirecting to signin");
    return <Navigate to="/signin" replace />;
  }

  if (!user) {
    console.log("❌ No user data - redirecting to signin");
    return <Navigate to="/signin" replace />;
  }

  if (!user.isActive) {
    console.log("❌ User not active - clearing session and redirecting to signin");
    localStorage.clear();
    return <Navigate to="/signin" replace />;
  }

  // Handle complete-profile routes with proper step validation
  if (window.location.pathname.startsWith('/complete-profile')) {
    console.log("🔄 On complete-profile route - checking step validity");
    
    const currentPath = window.location.pathname;
    const profileStatus = getProfileCompletionStatus(user);
    
    // Special handling for documents page - always allow access if user has completed required steps
    if (currentPath === '/complete-profile/documents') {
      // Check if user has completed the required steps before documents
      if (user.accountType === "organization") {
        // Organizations need: account type + phone
        if (!user.accountType || !user.phone) {
          console.log("🔄 Organization missing required steps before documents");
          return <Navigate to="/complete-profile" replace />;
        }
      } else if (user.accountType === "player") {
        // Players need: account type + phone + gender + birthday
        const hasGender = user.gender && user.gender.trim() !== '';
        const hasBirthday = user.metaData?.birthday || user.metaData?.hasCompletedBirthday;
        if (!user.accountType || !user.phone || !hasGender || !hasBirthday) {
          console.log("🔄 Player missing required steps before documents");
          return <Navigate to="/complete-profile" replace />;
        }
      } else {
        // Others need: account type + phone + gender
        const hasGender = user.gender && user.gender.trim() !== '';
        if (!user.accountType || !user.phone || !hasGender) {
          console.log("🔄 Other user type missing required steps before documents");
          return <Navigate to="/complete-profile" replace />;
        }
      }
      
      console.log("✅ User can access documents page");
      return children;
    }
    
    // For non-documents pages, if profile is complete, redirect to dashboard
    if (profileStatus.isComplete) {
      console.log("✅ Profile complete - redirecting to dashboard");
      return <Navigate to="/dashboard" replace />;
    }
    
    // Determine the correct next step URL
    let correctStepUrl = '/complete-profile';
    switch (profileStatus.nextStep) {
      case 1:
        correctStepUrl = '/complete-profile';
        break;
      case 2:
        correctStepUrl = '/complete-profile/phone';
        break;
      case 3:
        correctStepUrl = '/complete-profile/gender';
        break;
      case 4:
        correctStepUrl = '/complete-profile/birthday';
        break;
      case 5:
        correctStepUrl = '/complete-profile/documents';
        break;
      default:
        correctStepUrl = '/complete-profile/documents';
        break;
    }
    
    // If user is on wrong step, redirect to correct step
    if (currentPath !== correctStepUrl) {
      console.log(`🔄 Wrong step! Current: ${currentPath}, Should be: ${correctStepUrl}`);
      return <Navigate to={correctStepUrl} replace />;
    }
    
    console.log("✅ On correct complete-profile step - allowing access");
    return children;
  }

  // Check profile completion for ALL other private routes
  const profileStatus = getProfileCompletionStatus(user);
  if (!profileStatus.isComplete) {
    console.log(`❌❌❌ Profile INCOMPLETE - BLOCKING access to: ${window.location.pathname}`);
    console.log(`🔄 User needs to complete step ${profileStatus.nextStep} - Redirecting to /complete-profile`);
    return <Navigate to="/complete-profile" replace />;
  }

  console.log("✅ All checks passed - rendering component");
  return children;
};

export default PrivateRoute; 